// src/features/game/pages/CreateGamePage.tsx

import React, {
  useEffect,
  useState,
} from 'react';

import {
  useNavigate,
} from 'react-router-dom';

import {
  useSelector,
} from 'react-redux';

import {
  ArrowLeft,
  Plus,
} from 'lucide-react';

import type {
  RootState,
} from '../../../app/store';

import {
  useModal,
} from '../../../shared/components/ModalProvider';

import GameTemplateCard from '../components/GameTemplateCard';

import {
  createPlayGame,
  deleteGameTemplate,
  fetchGameTemplates,
} from '../services/gameService';

import type {
  GameTemplate,
} from '../types/game.types';

import RouteConfig from '../../../routes/RouteConfig';

const CreateGamePage:
  React.FC = () => {
  const navigate =
    useNavigate();

  const modal =
    useModal();

  const authUser =
    useSelector(
      (state: RootState) =>
        state.auth.user,
    );

  const [
    templates,
    setTemplates,
  ] =
    useState<GameTemplate[]>([]);

  const [
    templatesLoading,
    setTemplatesLoading,
  ] =
    useState(true);

  const [
    createLoadingId,
    setCreateLoadingId,
  ] =
    useState<number | null>(
      null,
    );

  const [
    deleteLoadingId,
    setDeleteLoadingId,
  ] =
    useState<number | null>(
      null,
    );

  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');

  /**
   * 템플릿 조회
   */
  const loadTemplates =
    async (): Promise<void> => {
      try {
        setTemplatesLoading(
          true,
        );

        setErrorMessage('');

        const data =
          await fetchGameTemplates();

        setTemplates(
          data,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '템플릿 조회 실패:',
          error,
        );

        setTemplates([]);

        setErrorMessage(
          '게임 목록을 불러오는데 실패했습니다.',
        );
      } finally {
        setTemplatesLoading(
          false,
        );
      }
    };

  useEffect(() => {
    void loadTemplates();
  }, []);

  /**
   * 템플릿 생성
   */
  const handleCreateTemplate =
    (): void => {
      navigate(
        RouteConfig.Management
          .TemplateCreate.fullPath,
      );
    };

  /**
   * 템플릿 수정
   */
  const handleEditTemplate = (
    gameId: number,
  ): void => {
    navigate(
      RouteConfig.Management
        .TemplateEdit.to(
          gameId,
        ),
    );
  };

  /**
   * 템플릿 삭제
   */
  const handleDeleteTemplate =
    async (
      gameId: number,
      gameName: string,
    ): Promise<void> => {
      const confirmed =
        await modal.confirm(
          `"${gameName}" 템플릿을 삭제하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setDeleteLoadingId(
          gameId,
        );

        await deleteGameTemplate(
          gameId,
        );

        setTemplates(
          (previous) =>
            previous.filter(
              (template) =>
                template.gameId !==
                gameId,
            ),
        );

        await modal.alert(
          '템플릿이 삭제되었습니다.',
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '템플릿 삭제 실패:',
          error,
        );

        await modal.alert(
          '템플릿 삭제에 실패했습니다.',
        );
      } finally {
        setDeleteLoadingId(
          null,
        );
      }
    };

  /**
   * 실제 게임 선택
   */
  const handleCreateGame =
    async (
      template: GameTemplate,
    ): Promise<void> => {
      if (
        !authUser?.storeId
      ) {
        setErrorMessage(
          '사용자 지점 정보를 확인할 수 없습니다.',
        );

        return;
      }

      const confirmed =
        await modal.confirm(
          `"${template.gameName}" 게임을 생성하시겠습니까?`,
        );

      if (!confirmed) {
        return;
      }

      try {
        setCreateLoadingId(
          template.gameId,
        );

        setErrorMessage('');

        await createPlayGame({
          storeId:
            authUser.storeId,

          gameId:
            template.gameId,

          gameSubtitle:
            template.gameName,
        });

        await modal.alert(
          '게임이 성공적으로 생성되었습니다.',
        );

        navigate(
          RouteConfig.Management
            .Games.fullPath,
        );
      } catch (
        error: unknown
      ) {
        console.error(
          '게임 선택 실패:',
          error,
        );

        setErrorMessage(
          '게임 선택에 실패했습니다. 다시 시도해주세요.',
        );
      } finally {
        setCreateLoadingId(
          null,
        );
      }
    };

  const isProcessing =
    createLoadingId !== null ||
    deleteLoadingId !== null;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">

        {/* Header */}
        <header className="mb-8 flex items-center gap-4">
          <button
            type="button"
            onClick={() =>
              navigate(
                RouteConfig.Management
                  .Games.fullPath,
              )
            }
            className="
              grid h-10 w-10
              place-items-center
              rounded-lg
              text-gray-600
              transition
              hover:bg-gray-100
              hover:text-gray-900
            "
            aria-label="게임 관리로 돌아가기"
          >
            <ArrowLeft
              size={24}
            />
          </button>

          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">
              게임 선택
            </h1>

            <p className="mt-1 text-sm text-gray-500">
              진행할 게임을 선택해주세요.
            </p>
          </div>
        </header>

        {/* Game Template List */}
        <section className="overflow-hidden rounded-xl bg-white shadow-sm">

          {/* Section Header */}
          <div className="flex items-center justify-between gap-4 border-b border-gray-200 p-5 sm:p-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                게임 선택
              </h2>

              <p className="mt-1 text-sm text-gray-500">
                생성, 수정 또는 삭제할 게임을 선택할 수 있습니다.
              </p>
            </div>

            <button
              type="button"
              onClick={
                handleCreateTemplate
              }
              className="
                flex shrink-0
                items-center gap-2
                rounded-lg
                bg-gray-900
                px-4 py-2
                text-sm font-medium
                text-white
                transition
                hover:bg-gray-700
              "
            >
              <Plus
                size={17}
              />

              게임 추가
            </button>
          </div>

          {templatesLoading ? (
            <div className="py-16 text-center text-gray-500">
              게임을 불러오는 중...
            </div>
          ) : templates.length ===
            0 ? (
            <div className="py-16 text-center">
              <p className="text-gray-500">
                등록된 게임이 없습니다.
              </p>

              <button
                type="button"
                onClick={
                  handleCreateTemplate
                }
                className="mt-4 text-main hover:underline"
              >
                첫 게임 추가하기
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">

              {/* Table Header */}
              <div
                className="
                  hidden
                  min-w-[850px]
                  grid-cols-[minmax(240px,2fr)_130px_140px_120px_110px_90px_90px]
                  border-b
                  border-gray-300
                  bg-gray-100
                  text-sm
                  font-semibold
                  text-gray-700
                  md:grid
                "
              >
                <div className="px-5 py-4">
                  게임명
                </div>

                <div className="px-4 py-4 text-center">
                  바이인
                </div>

                <div className="px-4 py-4 text-center">
                  시작 스택
                </div>

                <div className="px-4 py-4 text-center">
                  바이인 제한
                </div>

                <div className="px-4 py-4 text-center">
                  생성
                </div>

                <div className="px-4 py-4 text-center">
                  수정
                </div>

                <div className="px-4 py-4 text-center">
                  삭제
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-200">
                {templates.map(
                  (
                    template,
                  ) => (
                    <GameTemplateCard
                      key={
                        template.gameId
                      }
                      template={
                        template
                      }
                      onCreate={() =>
                        void handleCreateGame(
                          template,
                        )
                      }
                      onEdit={
                        handleEditTemplate
                      }
                      onDelete={
                        handleDeleteTemplate
                      }
                      isCreating={
                        createLoadingId ===
                        template.gameId
                      }
                      isDeleting={
                        deleteLoadingId ===
                        template.gameId
                      }
                      disabled={
                        isProcessing
                      }
                    />
                  ),
                )}
              </div>
            </div>
          )}
        </section>

        {/* Error */}
        {errorMessage && (
          <div className="mt-5 rounded-lg border border-red-300 bg-red-50 p-4">
            <p className="text-sm text-red-600">
              {errorMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateGamePage;